import { z } from 'zod';

import { createProposedActionsApi } from '@/shared/api/lotus';

import { defineToolSpec, toToolInputJsonSchema } from '../../types';
import {
  buildApplyModificationsErrorText,
  createUpdateFields,
  proposedActionPayloadSchema,
  toApiPayload,
  type ApiUpdateModification,
} from './shared';

const inputSchema = z.object({
  external_id: z.string().min(1),
  payload: proposedActionPayloadSchema,
  alternative_payloads: z.array(proposedActionPayloadSchema).optional(),
});

type UpdateProposedActionPayloadInput = z.infer<typeof inputSchema>;

const TOOL_NAME = 'update_proposed_action_payload';

export function createUpdateProposedActionPayloadToolSpec(patientUserId: string) {
  const api = createProposedActionsApi();

  return defineToolSpec({
    name: TOOL_NAME,
    inputSchema,
    getDefinition: () => ({
      name: TOOL_NAME,
      description:
        'Update the execution payload of one existing proposed action using a typed prescription, lab, or follow-up payload. ' +
        'Example input: {"external_id":"rx-lisinopril","payload":{"payload_tool_name":"draft_prescription","payload_id":"rx-1","payload_justification":"Adjust quantity","dispensableDrugId":12345,"directions":"Take 1 tablet by mouth daily","quantity":90,"refills":0,"daysSupply":90}}.',
      input_schema: toToolInputJsonSchema(inputSchema),
    }),
    async handle(input: UpdateProposedActionPayloadInput) {
      try {
        const fields = createUpdateFields({
          payload: toApiPayload(input.payload),
        });

        if (input.alternative_payloads !== undefined) {
          fields.alternatives = input.alternative_payloads.map(toApiPayload);
        }

        const modification: ApiUpdateModification = {
          type: 'update',
          external_id: input.external_id,
          fields,
        };

        const result = await api.applyModifications(patientUserId, [modification]);
        return { output: result };
      } catch (error: unknown) {
        return { errorText: buildApplyModificationsErrorText(error) };
      }
    },
  });
}
